import { useState } from 'react'
import { useParams } from 'react-router-dom'

import dayjs from 'dayjs'

import { useDeletePreviewApi, useDeleteRequestApi } from '@/common/api/modules/ugcCompliance'
import { DeleteRequestResponse } from '@/common/api/params/ugcCompliance'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

/**
 * 削除要望対応（GDPR/DSA）。
 *
 * お客様から「自分のデータを消してほしい」と言われたときの窓口。
 * 流れは必ず **①識別子を入力 → ②何が消えるかを確認 → ③削除を実行** の
 * 3段階にする。確認なしの即削除は取り返しがつかない——「確認した3件の
 * はずが5件消えた」を防ぐため、確認と削除はサーバ側で同じ探索条件を共有する。
 *
 * 削除の実行は監査ログに記録される（誰が・いつ・何件消したか）。
 * 監査ログに残す識別子は部分マスク——削除要求の趣旨からして、
 * 監査ログに生の個人情報を残すのは本末転倒。
 */

const input =
  'shadow-inset text-size-13 w-full rounded-lg border border-border-input bg-input px-3 py-2'

const fmt = (v: string | null) => (v ? dayjs(v).format('YYYY/MM/DD') : '—')

const UGCDeletionRequestPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)

  const [identifier, setIdentifier] = useState('')
  // プレビューは「確認する」を押したときの識別子で固定する。入力を変えたら確認からやり直し
  const [confirmedId, setConfirmedId] = useState<string | null>(null)
  const [result, setResult] = useState<DeleteRequestResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: preview, isLoading: previewLoading } = useDeletePreviewApi(
    confirmedId ? { site_id: siteId, user_identifier: confirmedId } : undefined
  )

  const { trigger: executeDelete, isMutating: deleting } = useDeleteRequestApi({
    onSuccess: (r) => {
      setResult(r)
      setConfirmedId(null)
      setError(null)
    },
    onError: (e) => setError(e instanceof Error ? e.message : '削除できませんでした')
  })

  const onConfirm = () => {
    const id = identifier.trim()
    if (!id) {
      setError('メールアドレスまたはお名前を入力してください')
      return
    }
    setError(null)
    setResult(null)
    setConfirmedId(id)
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='削除要望対応'
        description='お客様からの「自分のデータを消してほしい」というご要望に対応します。GDPR（EU一般データ保護規則）・DSA（EUデジタルサービス法）で求められる削除権への対応を含みます。'
      />

      <Content>
      <Area>
        <Heading subText='メールアドレスまたはお名前で、そのお客様に関するデータを探します。お名前は「さん」「様」などの敬称や空白を除いて一致するものを探します（一部だけの入力では探しません）。この時点では何も変更されません。'>
          ① 対象のお客様を指定
        </Heading>
        <AreaContent>
          <div className='flex flex-wrap items-center gap-3'>
            <input
              className={`${input} md:max-w-md`}
              value={identifier}
              placeholder='例）tanaka@example.co.jp または 田中 花子'
              onChange={(e) => {
                setIdentifier(e.target.value)
                // 入力を変えたら、前の確認結果では削除できないようにする
                setConfirmedId(null)
                setResult(null)
              }}
            />
            <Button color={ButtonColor.SECONDARY} onClick={onConfirm}>
              何が消えるかを確認する
            </Button>
            {error && (
              <Text size={12} color={TextColor.CRITICAL}>
                {error}
              </Text>
            )}
          </div>
        </AreaContent>
      </Area>

      {confirmedId && (
        <Area>
          <Heading subText='削除対象の一覧です。内容を照合してから、下の削除を実行してください。'>
            ② 消えるデータの確認
          </Heading>
          <AreaContent>
            {previewLoading || !preview ? (
              <Text color={TextColor.SECONDARY}>検索中...</Text>
            ) : preview.total === 0 ? (
              <Text size={13} color={TextColor.SECONDARY}>
                「{preview.identifier_masked}」に該当するデータは見つかりませんでした。
                メールアドレスの綴りや、お名前の表記（姓名そろっているか・漢字かひらがなか）をご確認ください。
              </Text>
            ) : (
              <div className='flex flex-col gap-4'>
                <Text size={13} weight='bold'>
                  {preview.identifier_masked} に関するデータ {preview.total} 件が削除（匿名化・非表示）されます
                </Text>
                {/* 2026-09-29 追加：お名前での検索は同姓同名の別人を含みうる */}
                {preview.matched_by === 'name' && (
                  <Text size={12} color={TextColor.CRITICAL}>
                    お名前で探した結果です。同姓同名の別のお客様のデータが含まれていないか、日付や内容を照合してください。
                    メールアドレスが分かる場合は、メールアドレスで探すほうが確実です。
                  </Text>
                )}

                {preview.reviews.length > 0 && (
                  <div>
                    <Text size={12} weight='bold'>
                      レビュー {preview.reviews.length} 件 — 氏名・メールを匿名化し、非表示にします
                    </Text>
                    <ul className='mt-1 list-disc pl-5'>
                      {preview.reviews.map((r) => (
                        <li key={r.id}>
                          <Text size={12} color={TextColor.SECONDARY}>
                            {fmt(r.created_at)} ／ ★{r.rating ?? '—'} ／ {r.title || '（無題）'}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {preview.mail_requests.length > 0 && (
                  <div>
                    <Text size={12} weight='bold'>
                      レビュー依頼メールの記録 {preview.mail_requests.length} 件 — 宛先・氏名を匿名化します
                    </Text>
                    <ul className='mt-1 list-disc pl-5'>
                      {preview.mail_requests.map((r) => (
                        <li key={r.id}>
                          <Text size={12} color={TextColor.SECONDARY}>
                            {fmt(r.created_at)} ／ {r.product_name || '—'}
                            {r.order_id ? ` ／ 受注 ${r.order_id}` : ''}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {preview.ugc_items.length > 0 && (
                  <div>
                    <Text size={12} weight='bold'>
                      UGC投稿 {preview.ugc_items.length} 件 — 投稿者名を匿名化し、非表示にします
                    </Text>
                    <ul className='mt-1 list-disc pl-5'>
                      {preview.ugc_items.map((r) => (
                        <li key={r.id}>
                          <Text size={12} color={TextColor.SECONDARY}>
                            {fmt(r.created_at)} ／ {r.source_type || '—'} ／ {r.caption || '（本文なし）'}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className='rounded-lg border border-border-caution bg-fill-caution px-4 py-3'>
                  <Text size={12} weight='bold'>
                    ③ 削除の実行（元に戻せません）
                  </Text>
                  <div className='mt-1'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      上の {preview.total} 件を匿名化・非表示にします。実行は監査ログに記録されます。
                    </Text>
                  </div>
                  <div className='mt-3'>
                    <Button
                      color={ButtonColor.CRITICAL}
                      loading={deleting}
                      disabled={deleting}
                      onClick={() =>
                        executeDelete({ site_id: siteId, user_identifier: confirmedId })
                      }
                    >
                      {preview.total} 件を削除する
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </AreaContent>
        </Area>
      )}

      {result && (
        <Area>
          <Heading>削除の完了</Heading>
          <AreaContent>
            <Text size={13}>
              {result.total_affected} 件を削除（匿名化・非表示）しました（レビュー{' '}
              {result.detail.reviews} 件／メール記録 {result.detail.mail_requests} 件／UGC{' '}
              {result.detail.ugc_items} 件）。
            </Text>
            <div className='mt-2'>
              <Text size={12} color={TextColor.SECONDARY}>
                この操作は監査ログに記録されました（システム管理 ＞ 監査ログ）。
                お客様への完了のご連絡を忘れずに行ってください。
              </Text>
            </div>
          </AreaContent>
        </Area>
      )}
      </Content>
    </Main>
  )
}

export default UGCDeletionRequestPage
